import { Locale as DateFnsLocale } from 'date-fns'
import {
  ar,
  de,
  enUS,
  es,
  id,
  it,
  ja,
  ko,
  ms,
  pl,
  pt,
  ru,
  th,
  tr,
  vi,
  zhCN,
  zhHK,
  zhTW,
} from 'date-fns/locale'
import { i18n } from 'i18next'
import Cookies from 'js-cookie'

import { getDomainName } from '@utils/cookie.utils'

import { ClientAreaLanguageDto } from '@model/ClientAreaLanguageDto'

export type Locale =
  | 'en'
  | 'zh'
  | 'it'
  | 'pl'
  | 'es'
  | 'th'
  | 'tr'
  | 'ar'
  | 'de'
  | 'id'
  | 'ko'
  | 'ms'
  | 'pt'
  | 'vi'
  | 'ru'
  | 'tw'
  | 'hk'
  | 'ja'

export type LocaleLabels = {
  label: string
  value: Locale
}

export type OverrideLocaleForZHLanguage =
  | 'en'
  | 'cn'
  | 'it'
  | 'pl'
  | 'es'
  | 'th'
  | 'tr'
  | 'ar'
  | 'de'
  | 'id'
  | 'ko'
  | 'ms'
  | 'pt'
  | 'vi'
  | 'ru'
  | 'tw'
  | 'hk'
  | 'ja'

export const AllLocales: ReadonlyArray<Locale> = [
  'en',
  'zh',
  'it',
  'pl',
  'es',
  'th',
  'tr',
  'ar',
  'de',
  'id',
  'ko',
  'ms',
  'pt',
  'vi',
  'ru',
  'tw',
  'hk',
  'ja',
]

export const LabeledLocales: ReadonlyArray<LocaleLabels> = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
  { value: 'pl', label: 'Polski' },
  { value: 'es', label: 'Español' },
  { value: 'th', label: 'ภาษาไทย' },
  { value: 'tr', label: 'Türkçe' },
  { value: 'ar', label: 'العربية' },
  { value: 'de', label: 'Deutsch' },
  { value: 'id', label: 'Bahasa Indonesia' },
  { value: 'ko', label: '한국어' },
  { value: 'ms', label: 'Bahasa Malaysia' },
  { value: 'pt', label: 'Português' },
  { value: 'vi', label: 'Tiếng Việt' },
  { value: 'ru', label: 'Русский' },
  { value: 'zh', label: '中文' },
  { value: 'tw', label: '中文繁體（臺灣）' },
  { value: 'hk', label: '中文繁體（香港）' },
  { value: 'ja', label: '日本語' },
]

export const defaultLocale: Locale = 'en'

function detectLocales(
  storedLocale: string | undefined,
  navigatorCandidates: ReadonlyArray<string>
): Locale {
  const navigatorLocales = navigatorCandidates.map((l) => l && l.split('-')[0])
  return (
    ([storedLocale, ...navigatorLocales].find((l) => AllLocales.includes(l as Locale)) as Locale) ||
    defaultLocale
  )
}

const localeCookieName = 'locale'

export function detectLocaleClientSide(): Locale {
  const storedLocale = Cookies.get(localeCookieName)
  return detectLocales(storedLocale, navigator.languages || [navigator.language])
}

export function storeLocaleClientSide(i18n: i18n, locale: string): void {
  Cookies.set(localeCookieName, locale, {
    expires: 999999,
    domain: getDomainName(),
  })
  i18n.changeLanguage(locale)
}

export const dateFnsLocales: Record<Locale, DateFnsLocale> = {
  en: enUS,
  zh: zhCN,
  it: it,
  pl: pl,
  es: es,
  th: th,
  tr: tr,
  ar: ar,
  de: de,
  id: id,
  ko: ko,
  ms: ms,
  pt: pt,
  vi: vi,
  ru: ru,
  tw: zhTW,
  hk: zhHK,
  ja: ja,
}

export const validateLanguageId = (
  languageId: string | undefined | null,
  availableLanguages: ClientAreaLanguageDto[] = []
): string => {
  const sanitizedLanguageId = (languageId ? languageId.trim() : '').replace(/[.,;:!?]$/, '')

  const isValidLanguage = availableLanguages.some(
    (lang) =>
      lang.id === sanitizedLanguageId ||
      lang.name.toLowerCase() === sanitizedLanguageId.toLowerCase() ||
      (sanitizedLanguageId.length > 2 &&
        lang.name.toLowerCase().includes(sanitizedLanguageId.toLowerCase()))
  )

  let matchedLanguageId = sanitizedLanguageId
  if (isValidLanguage && !availableLanguages.some((lang) => lang.id === sanitizedLanguageId)) {
    const matchedLang = availableLanguages.find(
      (lang) =>
        lang.name.toLowerCase() === sanitizedLanguageId.toLowerCase() ||
        (sanitizedLanguageId.length > 2 &&
          lang.name.toLowerCase().includes(sanitizedLanguageId.toLowerCase()))
    )
    if (matchedLang) {
      matchedLanguageId = matchedLang.id
    }
  }

  const defaultLanguageId =
    availableLanguages.find((lang) => lang.id === 'en')?.id ||
    (availableLanguages.length > 0 ? availableLanguages[0].id : 'en')

  return isValidLanguage ? matchedLanguageId : defaultLanguageId
}

export function mapAcuityLocale(locale: Locale): string {
  const locales: Record<Partial<Locale>, string> = {
    ar: 'ar-AE',
    en: 'en-GB',
    ru: 'ru-RU',
    es: 'es-ES',
    zh: 'zh-CN',
    it: 'it-IT',
    de: 'de-DE',
    pl: 'pl-PL',
    id: 'id-ID',
    pt: 'pt-PT',
    th: 'th-TH',
    vi: 'vi-vn',
    tr: 'en-GB', // missing
    ko: 'en-GB', // missing
    ms: 'en-GB', // missing
    tw: 'zh-CN',
    hk: 'zh-CN',
    ja: 'ja-JP',
  }

  if (locales[locale]) {
    return locales[locale]
  }
  return 'en-GB'
}

export const phraseLocaleFolder: Record<Locale, string> = {
  en: 'en',
  zh: 'zh',
  it: 'it',
  pl: 'pl',
  es: 'es',
  th: 'th',
  tr: 'tr',
  ar: 'ar',
  de: 'de',
  id: 'id',
  ko: 'ko',
  ms: 'ms',
  pt: 'pt',
  vi: 'vi',
  ru: 'ru',
  tw: 'zh-TW',
  hk: 'zh-HK',
  ja: 'ja',
}

export function mapToTradingViewLocale(locale: Locale): string {
  const localeMap: Record<Locale, string> = {
    en: 'en',
    zh: 'zh_CN',
    it: 'it',
    pl: 'pl',
    es: 'es',
    th: 'th_TH',
    tr: 'tr',
    ar: 'ar_AE',
    de: 'de_DE',
    id: 'id',
    ko: 'kr',
    ms: 'ms_MY',
    pt: 'br',
    vi: 'vi_VN',
    ru: 'ru',
    tw: 'zh_TW',
    hk: 'zh_TW',
    ja: 'ja',
  }

  return localeMap[locale] || 'en'
}
